import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator as CalcIcon, ArrowRight } from "@phosphor-icons/react";

const currency = (n) =>
  Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

function recommend({ income, debt, family }) {
  const inc = Number(income) || 0;
  const d = Number(debt) || 0;
  const f = Number(family) || 0;

  const coverage = Math.max(inc * 10 + d + f * 50000, 50000);

  // Term when the need is largely temporary (mortgage/income replacement);
  // lean Permanent when debt is low relative to income (legacy / accumulation).
  const temporaryNeed = d + inc * 8;
  const type = d > inc * 2 || f >= 2
    ? { primary: "Term Life", note: "20–30 year term covers your mortgage and child-raising years at the lowest cost." }
    : { primary: "Permanent Life (IUL)", note: "With lower debt, permanent coverage adds lifelong protection plus tax-advantaged cash value." };

  return { coverage, type, temporaryNeed };
}

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Calculator() {
  const [inputs, setInputs] = useState({ income: 75000, debt: 250000, family: 2 });
  const [shown, setShown] = useState(false);
  const set = (k, v) => setInputs((s) => ({ ...s, [k]: v }));
  const result = useMemo(() => recommend(inputs), [inputs]);

  return (
    <section id="calculator" className="bg-warm-white py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <p className="eyebrow text-gold mb-4 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-gold" /> Coverage Calculator
          </p>
          <h2 className="font-heading font-extrabold tracking-tighter text-navy text-[clamp(2rem,4.5vw,3.4rem)] leading-[1]">
            How much coverage do I need?
          </h2>
          <p className="mt-4 text-brand-slate leading-relaxed">
            Three quick numbers for an instant estimate and whether term or permanent life fits —
            then book a free 15-minute call to lock in the details. Educational only.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-black/[0.07] bg-white p-7 sm:p-9" data-testid="calculator-widget">
            <div className="space-y-8">
              <Slider label="Annual income" value={inputs.income} min={0} max={500000} step={5000}
                format={currency} testid="calc-income" onChange={(v) => set("income", v)} />
              <Slider label="Debt & mortgage" value={inputs.debt} min={0} max={1000000} step={10000}
                format={currency} testid="calc-debt" onChange={(v) => set("debt", v)} />
              <Slider label="Family members to protect" value={inputs.family} min={0} max={8}
                testid="calc-family" onChange={(v) => set("family", v)} />

              <button
                data-testid="calc-submit"
                onClick={() => setShown(true)}
                className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-warm-white font-semibold transition-all hover:bg-slate-navy active:scale-[0.98]"
              >
                Estimate my coverage
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>

          <motion.div
            key={shown ? "result" : "placeholder"}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="rounded-2xl bg-navy grain relative overflow-hidden p-7 sm:p-9 flex flex-col"
            data-testid="calculator-result"
          >
            <div className="pointer-events-none absolute -top-16 -right-16 w-52 h-52 rounded-full bg-gold/10 blur-2xl" />
            {!shown ? (
              <div className="relative z-10 flex-1 flex flex-col items-start justify-center text-left">
                <span className="grid place-items-center w-14 h-14 rounded-xl bg-white/5 text-gold mb-5">
                  <CalcIcon weight="duotone" size={30} />
                </span>
                <p className="text-light-slate max-w-sm">
                  Enter your numbers and press <span className="text-warm-white font-semibold">Estimate
                  my coverage</span> to see a recommended amount and policy type.
                </p>
              </div>
            ) : (
              <div className="relative z-10 flex-1 flex flex-col">
                <p className="eyebrow text-gold">Recommended coverage</p>
                <p className="font-heading font-extrabold text-warm-white text-[clamp(2.4rem,6vw,3.6rem)] leading-none mt-3"
                  data-testid="calc-coverage-amount">
                  {currency(result.coverage)}
                </p>
                <div className="mt-6 rounded-xl bg-white/5 border border-white/10 p-5">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-light-slate">Best-fit policy</p>
                  <p className="font-heading font-bold text-warm-white text-xl mt-1" data-testid="calc-policy-type">
                    {result.type.primary}
                  </p>
                  <p className="text-sm text-light-slate mt-2 leading-relaxed">{result.type.note}</p>
                </div>

                <button
                  data-testid="calc-to-booking"
                  onClick={() => scrollTo("booking")}
                  className="mt-auto pt-8 group inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-navy font-semibold text-sm w-max hover:brightness-110 transition-all"
                >
                  Book your free 15-min consultation
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Slider({ label, value, min, max, step = 1, onChange, format, testid }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <label className="text-[11px] uppercase tracking-[0.18em] text-brand-slate">{label}</label>
        <span className="font-heading font-bold text-navy text-lg" data-testid={`${testid}-value`}>
          {format ? format(value) : value}
        </span>
      </div>
      <input
        type="range"
        data-testid={testid}
        min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1.5 appearance-none rounded-full outline-none cursor-pointer
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-navy [&::-webkit-slider-thumb]:cursor-pointer
          [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-warm-white [&::-webkit-slider-thumb]:shadow"
        style={{ background: `linear-gradient(to right, #0B192C ${pct}%, #E2E8F0 ${pct}%)` }}
      />
    </div>
  );
}
